import { useEffect, useState, useCallback, useRef } from "react";
import { Bell, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { crmApi, formatDate } from "../../lib/crm";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = useRef(null);

  const fetch = useCallback(async () => {
    try {
      const r = await crmApi.get("/notifications");
      setData(r.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("[NotificationBell] fetch failed:", err);
      }
    }
  }, []);

  useEffect(() => {
    fetch();
    const id = setInterval(fetch, 30000); // poll every 30s
    return () => clearInterval(id);
  }, [fetch]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    try {
      await crmApi.post(`/notifications/${id}/read`);
      fetch();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("[NotificationBell] markRead failed:", err);
      }
    }
  };

  const markAllRead = async () => {
    try {
      await crmApi.post("/notifications/read-all");
      fetch();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("[NotificationBell] markAllRead failed:", err);
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell-btn"
        className="relative p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span
            data-testid="notif-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center"
          >
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 top-full mt-2 w-80 max-h-[400px] overflow-y-auto glass-strong border border-white/10 rounded-xl shadow-2xl z-50"
        >
          <div className="flex items-center justify-between p-3 border-b border-white/8">
            <span className="text-xs font-bold uppercase tracking-wider text-white/80">Notifications</span>
            {data.unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="notif-mark-all-read"
                className="text-[10px] text-lime-400 hover:text-lime-300"
              >
                Mark all read
              </button>
            )}
          </div>
          {data.items.length === 0 ? (
            <div className="p-6 text-center text-sm text-white/40">No notifications</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {data.items.map((n) => (
                <li key={n.id} className={`p-3 hover:bg-white/[0.03] ${!n.read ? "bg-lime-400/5" : ""}`} data-testid={`notif-item-${n.id}`}>
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 mt-2 rounded-full bg-lime-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      {n.link ? (
                        <Link
                          to={n.link}
                          onClick={() => {
                            markRead(n.id);
                            setOpen(false);
                          }}
                          className="block"
                        >
                          <div className="text-sm font-medium text-white truncate">{n.title}</div>
                          {n.body && <div className="text-xs text-white/50 truncate">{n.body}</div>}
                        </Link>
                      ) : (
                        <div>
                          <div className="text-sm font-medium text-white truncate">{n.title}</div>
                          {n.body && <div className="text-xs text-white/50 truncate">{n.body}</div>}
                        </div>
                      )}
                      <div className="text-[10px] text-white/30 mt-1 mono">{formatDate(n.created_at)}</div>
                    </div>
                    {!n.read && (
                      <button
                        onClick={() => markRead(n.id)}
                        title="Mark as read"
                        className="text-white/40 hover:text-emerald-400 p-1"
                      >
                        <Check className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
